/**
 * The before/after view a structured AI edit shows before it is applied.
 *
 * `reviseStructuredGuide` answers with the WHOLE edited envelope and a list of the top-level keys
 * it changed. Writing that straight into the editor is what the HTML chat does, but a template
 * guide has no live preview to glance at, so a changed field can drop a paragraph or reshuffle a
 * table with nothing on screen to show it. This turns the two envelopes plus `changedFields` into
 * a per-field diff the operator confirms against: prose fields as before/after text, list and
 * table fields as the rows added and removed.
 *
 * It reuses `parseGuide` so a body the editor already rejected as invalid JSON produces no diff
 * rather than a throw, and `FIELD_MAP`/`SECTION_LABELS` so each field is named the way the rest of
 * the editor names it rather than by its camelCase key.
 */
import { SECTION_LABELS, parseGuide } from '../sections';
import { FIELD_MAP } from './fieldMap';

export interface FieldDiff {
  key: string;
  /** The human label the editor shows for this top-level section. */
  label: string;
  /** `text` renders before/after prose; `list` renders added/removed rows. */
  kind: 'text' | 'list';
  /** Flattened prose, for a `text` diff. Empty when the field was added or removed. */
  before: string;
  after: string;
  /** Rows (as readable lines) present after but not before, and before but not after. */
  added: string[];
  removed: string[];
}

/** The label the editor uses for a top-level key, or the key itself when nothing names it. */
export function fieldLabel(pageType: string, key: string): string {
  return FIELD_MAP[pageType]?.[key]?.label ?? SECTION_LABELS[key] ?? key;
}

/** Every string leaf of a value, in document order, tags stripped: the prose a reader sees. */
function stringLeaves(value: unknown, out: string[] = []): string[] {
  if (typeof value === 'string') out.push(value);
  else if (Array.isArray(value)) value.forEach((v) => stringLeaves(v, out));
  else if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>).forEach((v) => stringLeaves(v, out));
  }
  return out;
}

const stripTags = (s: string): string => s.replace(/<[^>]*>/g, '').replace(/&[a-z]+;/gi, ' ');

/** A value as one block of readable prose, for a text diff. */
function toText(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return stripTags(value).trim();
  return stringLeaves(value)
    .map((s) => stripTags(s).trim())
    .filter(Boolean)
    .join('\n');
}

/** One readable line for a list item: the string itself, or its leaves joined with a separator. */
function itemLine(item: unknown): string {
  if (typeof item === 'string') return stripTags(item).trim();
  if (Array.isArray(item)) {
    // A table row is an array of cells; show it as the cells joined.
    return item.map((c) => stripTags(String(c ?? '')).trim()).join(' | ');
  }
  if (item && typeof item === 'object') {
    return stringLeaves(item)
      .map((s) => stripTags(s).trim())
      .filter(Boolean)
      .join(' | ');
  }
  return String(item ?? '');
}

/** Every row of a list/table value, as readable lines (a table diffs on its `rows`). */
function toLines(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(itemLine).filter(Boolean);
  if (value && typeof value === 'object' && Array.isArray((value as Record<string, unknown>).rows)) {
    return ((value as Record<string, unknown>).rows as unknown[]).map(itemLine).filter(Boolean);
  }
  return [];
}

/** Whether a field should diff as a list of rows rather than as prose. */
function isListLike(value: unknown): boolean {
  if (Array.isArray(value)) return true;
  return !!value && typeof value === 'object' && Array.isArray((value as Record<string, unknown>).rows);
}

/** The rows in `b` not in `a`, compared as readable lines (order-insensitive, duplicates kept). */
function missingFrom(a: string[], b: string[]): string[] {
  const pool = [...a];
  const out: string[] = [];
  for (const line of b) {
    const i = pool.indexOf(line);
    if (i === -1) out.push(line);
    else pool.splice(i, 1);
  }
  return out;
}

/** The top-level keys whose serialised value differs between the two envelopes. */
export function changedKeys(prevObj: Record<string, unknown>, nextObj: Record<string, unknown>): string[] {
  const keys = new Set([...Object.keys(prevObj), ...Object.keys(nextObj)]);
  const out: string[] = [];
  for (const key of keys) {
    if (JSON.stringify(prevObj[key]) !== JSON.stringify(nextObj[key])) out.push(key);
  }
  return out;
}

/**
 * The per-field diff for the confirm panel.
 *
 * `changedFields` is what the backend reported; an empty list falls back to comparing the two
 * envelopes key by key, so a confirm panel is shown even if the field list did not arrive. A key
 * whose value is identical in both (the backend named it but it did not actually move, or a
 * reverted field) is dropped, so the panel never shows a change that is not one.
 */
export function computeFieldDiffs(
  prevJson: string,
  nextJson: string,
  changedFields: string[],
  pageType: string,
): FieldDiff[] {
  const prev = parseGuide(prevJson);
  const next = parseGuide(nextJson);
  if (!prev.ok || !next.ok) return [];

  const keys =
    changedFields && changedFields.length
      ? changedFields.filter((k) => JSON.stringify(prev.obj[k]) !== JSON.stringify(next.obj[k]))
      : changedKeys(prev.obj, next.obj);

  const diffs: FieldDiff[] = [];
  for (const key of keys) {
    const beforeVal = prev.obj[key];
    const afterVal = next.obj[key];
    const label = fieldLabel(pageType, key);

    if (isListLike(beforeVal) || isListLike(afterVal)) {
      const beforeLines = toLines(beforeVal);
      const afterLines = toLines(afterVal);
      diffs.push({
        key,
        label,
        kind: 'list',
        before: '',
        after: '',
        added: missingFrom(beforeLines, afterLines),
        removed: missingFrom(afterLines, beforeLines),
      });
    } else {
      diffs.push({
        key,
        label,
        kind: 'text',
        before: toText(beforeVal),
        after: toText(afterVal),
        added: [],
        removed: [],
      });
    }
  }
  return diffs;
}
